import { Button, Input } from '@insify/design-system/components';
import { textMd } from '@insify/design-system/typography';
import { useBreakpoint } from '@insify/react-hooks';
import { TrackedAnchor } from '@objectiv/tracker-react';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { sendLoginEmail } from '../../api/sendLoginEmail';
import { getAppDomain } from '../../utils/localize';

import * as styles from './SendLinkForm.module.css';

import type { SupportedCountry } from '../../types/supported-countries';

const SendLinkForm: React.FC = () => {
  const { t, i18n } = useTranslation('common');

  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const language = i18n.language;
  const domain = getAppDomain();

  if (!['fr', 'nl', 'de'].includes(domain)) {
    console.error(`Invalid domain: ${domain}`);
    return null;
  }

  const { mutate: sendEmail, isPending } = useMutation({
    mutationFn: (email: string) => sendLoginEmail(email, language, domain as SupportedCountry),
    onSuccess: () => navigate('/email-sent', { state: { email } }),
    onError: (error) => setError(error.message),
  });

  const isMobile = !useBreakpoint('(min-width: 960px)');

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    sendEmail(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <Input
        type='email'
        id='email'
        trackingId='email'
        name='email'
        autoComplete='email'
        error={error}
        disabled={isPending}
        className={styles.inputContainer}
        inputClassName={styles.input}
        label={t('login_link_request_form_email')}
        hasNoBottomContent
        value={email}
        onChange={handleChange}
      />

      <div className={styles.btnContainer}>
        <Button
          trackingId='login'
          type='submit'
          kind={isMobile ? 'neutral' : 'primary'}
          className={styles.submitButton}
          loading={isPending}
          disabled={isPending || !email}
        >
          {t('login_link_request_form_submit')}
        </Button>
        <TrackedAnchor
          objectiv={{ id: 'forgot-email' }}
          onClick={() => navigate('/forgot-email')}
          className={clsx(textMd, styles.forgotPassword)}
        >
          {t('login_link_forgot_mail_title')}
        </TrackedAnchor>
      </div>
    </form>
  );
};

export { SendLinkForm };
